import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Hook for the Menus browse page (Phase 17, Plan 17-03).
 *
 * Mirrors useFilterLabels.js with ONE additive delta:
 *  - NEW linkType state ('all' | 'MENU' | 'LINK') passed as query param;
 *    backend filters WHERE link_type = $linkType when value != 'all'.
 *
 * All other behaviour (status filter, policy filter, search debounce,
 * optimistic policy PATCH with rollback, sync trigger) is preserved verbatim.
 */
export default function useMenuLinks() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side)
  const [linkType, setLinkType] = useState('all');     // NEW (Phase 17): 'all' | 'MENU' | 'LINK'
  const [policy, setPolicy] = useState('all');         // 'translate' | 'skip' | 'auto-skipped' | 'all'
  const [status, setStatus] = useState('all');         // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches Phase 14/16 precedent)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (linkType && linkType !== 'all') params.link_type = linkType;   // NEW (Phase 17)
      if (policy && policy !== 'all') params.policy = policy;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/menu-links/', { params }),
        axios.get('/api/transly/menu-links/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load menu links');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, linkType, policy, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages)
  useEffect(() => {
    setPage(1);
  }, [linkType, policy, status, debouncedQ]);   // NEW: linkType included

  const updatePolicy = useCallback(async (menuLinkId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === menuLinkId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/menu-links/${menuLinkId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch this row's truth.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/menu-links/sync/');
  }, []);

  return {
    // Data
    rows,
    total,
    loading,
    error,
    // Filters
    linkType,                // NEW (Phase 17)
    setLinkType,             // NEW (Phase 17)
    policy,
    setPolicy,
    status,
    setStatus,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
  };
}
